import React, { createContext, useContext, useState, useEffect } from 'react';
import type { LayoutManifest, ManifestCategory, ManifestModule, MenuPlacement, ModuleOverrideConfig, AppMode } from '../types';
import { ApiClient } from '../api/client';
import { useAuth } from './AuthContext';
import { effectiveUiRole } from '../utils/roleLabels';

const STORAGE_KEY = 'kf_layout_menu_overrides';
const GLOBAL_STORAGE_KEY = 'kf_layout_global_overrides';
const TENANT_STORAGE_KEY = 'kf_layout_tenant_overrides';
const DELETED_MODULES_KEY = 'kf_layout_deleted_modules';
const PURGED_MODULES_KEY = 'kf_layout_purged_modules';

// Műveletekkel és aktív funkciókkal rendelkező modulok nyilvántartása
export const MODULE_ASSIGNED_OPERATIONS: Record<string, string> = {
  records_manager: 'Adatlapok és rekordok kezelése, csatolmányok',
  pages_admin: 'Közzétételi oldalak és meződefiníciók adminisztrációja',
  quick_wizard: '3-lépéses adatrögzítési varázsló modal',
  export_tools: 'XLSX, CSV, PDF és archívum export funkciók',
  sync_manager: 'Többcélú felhő- és helyi szinkronizáció',
  smart_zip_importer: 'ZIP mappastruktúra feltöltés és staging motor',
  web_scraper: 'Weboldal feltérképező és adatkinyerő scraper',
  personnel_manager: 'Szervezeti egységek, vezetők és személyzet nyilvántartás',
  linked_entities: 'Kapcsolt szervek és intézmények hálózata',
  access_groups: 'Intézményi csoportok: a tagok csak a hozzájuk rendelt alpontokat kezelik',
  text_replacer: 'Tömeges szövegcsere és regex feldolgozó',
  compliance_dashboard: 'Megfelelőségi kimutatások és riportok',
  regulations_admin: 'Jogszabálygyűjtemény és Nemzeti Jogtár kapcsolatok',
  user_admin: 'Felhasználók és RBAC jogosultságok kezelése',
  audit_log: 'Rendszerszintű műveleti napló (audit trail)',
  tenants_admin: 'Bérlők és önkormányzatok regisztrációja',
  system_monitoring: 'Rendszererőforrás és tároló monitorozás',
  branding_settings: 'Arculati beállítások és megjelenés',
  upload_policy: 'Fájlméret kvóták és tárolási szabályzatok',
  backup_restore: 'Adatbázis mentési és helyreállítási funkciók',
  schema_maintenance: 'Adatbázis-séma ellenőrzése, teljes mentés és egylépéses sémafrissítés',
  system_announcements: 'Rendszerüzenetek: fényújság és tájékoztató belépés után és a belépési oldalakon',
  lockdown_panel: 'Zárt üzemmód, külső elérhetőség és megbízható hálózatok',
  ticket_management: 'NIS2 hibajegyek és bejelentések kezelése',
  help_center: 'Kereshető rendszerdokumentáció és felhasználói súgó',
  public_portal: 'Nyilvános közzétételi portál',
};

export const checkModuleOperation = (moduleId: string): { hasOperation: boolean; reason?: string } => {
  if (moduleId in MODULE_ASSIGNED_OPERATIONS) {
    return { hasOperation: true, reason: MODULE_ASSIGNED_OPERATIONS[moduleId] };
  }
  return { hasOperation: false };
};

// Alapértelmezett intelligens elhelyezés, ha nincs megadva a manifestben
const getDefaultPlacement = (mod: ManifestModule): MenuPlacement => {
  if (mod.placement) return mod.placement;
  if (['quick_wizard', 'public_portal'].includes(mod.id)) return 'navbar_direct';
  if (['sync_manager', 'export_tools', 'ticket_management', 'help_center'].includes(mod.id)) return 'navbar_tools';
  return 'sidebar';
};

interface LayoutContextType {
  manifest: LayoutManifest | null;
  categories: ManifestCategory[];
  modules: ManifestModule[];
  effectiveModules: ManifestModule[];
  deletedModules: ManifestModule[];
  deletedModuleIds: string[];
  activeCategoryId: string;
  activeModuleId: string;
  activePageId: number | null;
  setActiveCategory: (catId: string) => void;
  setActiveModule: (modId: string) => void;
  setActivePageId: (pageId: number | null) => void;
  isLoading: boolean;
  refreshLayout: () => Promise<void>;
  
  // Menü & Elhelyezés Testreszabás
  overrides: Record<string, ModuleOverrideConfig>;
  globalOverrides: Record<string, ModuleOverrideConfig>;
  tenantOverrides: Record<string, ModuleOverrideConfig>;
  getModulesByPlacement: (placement: MenuPlacement, filterAppMode?: AppMode, targetRole?: string) => ManifestModule[];
  saveModuleOverride: (moduleId: string, config: ModuleOverrideConfig) => void;
  saveAllOverrides: (newOverrides: Record<string, ModuleOverrideConfig>) => void;
  saveGlobalOverrides: (newOverrides: Record<string, ModuleOverrideConfig>, newDeletedIds?: string[]) => Promise<void>;
  saveTenantOverrides: (newOverrides: Record<string, ModuleOverrideConfig>) => Promise<void>;
  resetToCentralDefaults: () => Promise<void>;
  resetToFactoryDefaults: () => Promise<void>;
  resetAllOverrides: () => void;
  deleteModule: (moduleId: string) => void;
  restoreModule: (moduleId: string) => void;
  restoreAllModules: () => void;
  purgeModule: (moduleId: string) => void;
  purgeAllDeletedModules: () => void;
  hasModuleOperation: (moduleId: string) => { hasOperation: boolean; reason?: string };
  isConfiguratorOpen: boolean;
  setIsConfiguratorOpen: (open: boolean) => void;
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
  canAccessModule: (moduleId: string, targetRole?: string) => boolean;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, role, appMode } = useAuth();
  const [manifest, setManifest] = useState<LayoutManifest | null>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<string>('transparency_data');
  const [activeModuleId, setActiveModuleId] = useState<string>('records_manager');
  const [activePageId, setActivePageId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfiguratorOpen, setIsConfiguratorOpen] = useState(false);

  // Tényleges szerepkör (a szerverrel azonos szabály): a granular_role csak szűkíthet, emelni nem tud.
  const effRole = user ? effectiveUiRole(user) : ((role || 'public') as string);
  const isSuperAdmin = effRole === 'superadmin';
  const isOrgAdmin = effRole === 'org_admin';
  const currentRole = effRole;

  // Törölt modulok azonosítói
  const [deletedModuleIds, setDeletedModuleIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(DELETED_MODULES_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Véglegesen törölt (kiiktatott) modulok azonosítói
  const [purgedModuleIds, setPurgedModuleIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(PURGED_MODULES_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Globális (Szuperadmin) felülbírálások
  const [globalOverrides, setGlobalOverrides] = useState<Record<string, ModuleOverrideConfig>>(() => {
    try {
      const saved = localStorage.getItem(GLOBAL_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Szervezeti (Org Admin) felülbírálások
  const [tenantOverrides, setTenantOverrides] = useState<Record<string, ModuleOverrideConfig>>(() => {
    try {
      const saved = localStorage.getItem(TENANT_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
      const fallback = localStorage.getItem(STORAGE_KEY);
      return fallback ? JSON.parse(fallback) : {};
    } catch {
      return {};
    }
  });

  const refreshLayout = async () => {
    setIsLoading(true);
    try {
      const [layoutRes, overridesRes] = await Promise.all([
        ApiClient.getLayout(),
        ApiClient.getLayoutOverrides(),
      ]);

      if (layoutRes.ok && layoutRes.data) {
        setManifest(layoutRes.data);
        if (layoutRes.data.categories.length > 0 && !activeCategoryId) {
          setActiveCategoryId(layoutRes.data.categories[0].id);
        }
      }

      if (overridesRes.ok && overridesRes.data) {
        if (overridesRes.data.globalOverrides) {
          setGlobalOverrides(overridesRes.data.globalOverrides);
          localStorage.setItem(GLOBAL_STORAGE_KEY, JSON.stringify(overridesRes.data.globalOverrides));
        }
        if (overridesRes.data.tenantOverrides) {
          setTenantOverrides(overridesRes.data.tenantOverrides);
          localStorage.setItem(TENANT_STORAGE_KEY, JSON.stringify(overridesRes.data.tenantOverrides));
        }
        if (Array.isArray(overridesRes.data.deletedModuleIds)) {
          setDeletedModuleIds(overridesRes.data.deletedModuleIds);
          localStorage.setItem(DELETED_MODULES_KEY, JSON.stringify(overridesRes.data.deletedModuleIds));
        }
        if (Array.isArray(overridesRes.data.purgedModuleIds)) {
          setPurgedModuleIds(overridesRes.data.purgedModuleIds);
          localStorage.setItem(PURGED_MODULES_KEY, JSON.stringify(overridesRes.data.purgedModuleIds));
        }
      }
    } catch (err) {
      console.error('Hiba a menüstruktúra lekérésekor:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshLayout();
  }, [user]);

  const categories = manifest?.categories || [];
  const fetchedModules = manifest?.modules || [];

  // Garantáljuk, hogy a help_center mindig jelen legyen a modulok között (mind a 24 modul megmarad!)
  const rawModules: ManifestModule[] = fetchedModules.some((m) => m.id === 'help_center')
    ? fetchedModules
    : [
        ...fetchedModules,
        {
          id: 'help_center',
          category: 'compliance_reports',
          title: 'Súgó & Útmutatók',
          icon: 'HelpCircle',
          description: 'Kereshető rendszerdokumentáció, jogszabályi útmutatók és súgó',
          component: 'HelpCenterModal',
          container: 'wizard_modal',
          mode: 'both',
          order: 9,
          placement: 'navbar_tools',
          roles: ['user', 'operator', 'editor', 'org_admin', 'superadmin', 'auditor', 'tenant_admin'],
        },
      ];

  // A véglegesen törölt (kiiktatott) modulok sehol nem jelennek meg
  const availableModules = rawModules.filter((mod) => !purgedModuleIds.includes(mod.id));

  // Hatásos modulok összefésülése a felülbírálásokkal
  const effectiveModules: ManifestModule[] = availableModules
    .filter((mod) => !deletedModuleIds.includes(mod.id))
    .map((mod) => {
      const defaultTitle = mod.title;
      const gOvr = globalOverrides[mod.id];
      const tOvr = tenantOverrides[mod.id];

      const globalTitle = (gOvr?.global_title || gOvr?.custom_title || '').trim();
      const tenantTitle = (tOvr?.custom_title || '').trim();

      // Megnevezés hierarchia:
      // 1. Ha a szervezet egyedi nevet adott és nem szuperadmin: tenantTitle
      // 2. Egyébként ha van szuperadmin globális név: globalTitle
      // 3. Egyébként a kód gyári neve: defaultTitle
      let effectiveTitle = defaultTitle;
      if (isSuperAdmin) {
        effectiveTitle = globalTitle || defaultTitle;
      } else {
        if (tenantTitle) {
          effectiveTitle = tenantTitle;
        } else if (globalTitle) {
          effectiveTitle = globalTitle;
        } else {
          effectiveTitle = defaultTitle;
        }
      }

      const defaultCategory = mod.category;
      const defaultPlacement = getDefaultPlacement(mod);
      const activeOvr = (!isSuperAdmin && tOvr && Object.keys(tOvr).length > 0) ? tOvr : (gOvr || tOvr);
      const placement = activeOvr?.placement ?? gOvr?.placement ?? defaultPlacement;
      const category = activeOvr?.category ?? gOvr?.category ?? defaultCategory;
      const roles = activeOvr?.roles ?? gOvr?.roles ?? mod.roles;
      const mode = activeOvr?.mode ?? gOvr?.mode ?? mod.mode;
      const order = activeOvr?.order ?? gOvr?.order ?? mod.order;

      return {
        ...mod,
        title: effectiveTitle,
        default_title: defaultTitle,
        default_category: defaultCategory,
        category,
        global_title: globalTitle || undefined,
        custom_title: tenantTitle || undefined,
        placement,
        roles,
        mode,
        order,
      };
    });

  // Törölt modulok listája visszaállítási lehetőséghez
  const deletedModules: ManifestModule[] = availableModules
    .filter((mod) => deletedModuleIds.includes(mod.id))
    .map((mod) => ({ ...mod, placement: 'hidden' as MenuPlacement }));

  // Modul törlése (csak ha nincs hozzárendelve művelet!)
  const deleteModule = (moduleId: string) => {
    const { hasOperation, reason } = checkModuleOperation(moduleId);
    if (hasOperation) {
      throw new Error(`A menüpont nem törölhető, mert aktív művelet van hozzárendelve: ${reason}`);
    }
    setDeletedModuleIds((prev) => {
      if (prev.includes(moduleId)) return prev;
      const updated = [...prev, moduleId];
      localStorage.setItem(DELETED_MODULES_KEY, JSON.stringify(updated));
      if (isSuperAdmin) {
        saveGlobalOverrides(globalOverrides, updated);
      }
      return updated;
    });
  };

  // Törölt modul visszaállítása
  const restoreModule = (moduleId: string) => {
    setDeletedModuleIds((prev) => {
      const updated = prev.filter((id) => id !== moduleId);
      localStorage.setItem(DELETED_MODULES_KEY, JSON.stringify(updated));
      if (isSuperAdmin) {
        saveGlobalOverrides(globalOverrides, updated);
      }
      return updated;
    });
  };

  // Összes törölt modul visszaállítása
  const restoreAllModules = () => {
    setDeletedModuleIds([]);
    localStorage.removeItem(DELETED_MODULES_KEY);
    if (isSuperAdmin) {
      saveGlobalOverrides(globalOverrides, []);
    }
  };

  // Menüpont végleges törlése (kikerül a törölt elemek közül is és sehol sem foglal helyet)
  const purgeModule = (moduleId: string) => {
    const updatedDeleted = deletedModuleIds.filter((id) => id !== moduleId);
    const updatedPurged = purgedModuleIds.includes(moduleId)
      ? purgedModuleIds
      : [...purgedModuleIds, moduleId];

    setDeletedModuleIds(updatedDeleted);
    setPurgedModuleIds(updatedPurged);
    localStorage.setItem(DELETED_MODULES_KEY, JSON.stringify(updatedDeleted));
    localStorage.setItem(PURGED_MODULES_KEY, JSON.stringify(updatedPurged));

    if (isSuperAdmin) {
      saveGlobalOverrides(globalOverrides, updatedDeleted, updatedPurged);
    }
  };

  // Összes törölt menüpont végleges törlése (kiürítés)
  const purgeAllDeletedModules = () => {
    const toPurge = deletedModules.map((m) => m.id);
    const updatedPurged = Array.from(new Set([...purgedModuleIds, ...toPurge]));

    setDeletedModuleIds([]);
    setPurgedModuleIds(updatedPurged);
    localStorage.removeItem(DELETED_MODULES_KEY);
    localStorage.setItem(PURGED_MODULES_KEY, JSON.stringify(updatedPurged));

    if (isSuperAdmin) {
      saveGlobalOverrides(globalOverrides, [], updatedPurged);
    }
  };

  // Szerepkör hozzáférés ellenőrzése
  const checkRoleAccess = (userRole: string, allowedRoles?: string[]) => {
    if (!allowedRoles || allowedRoles.length === 0) return true;
    if (userRole === 'superadmin' || userRole === 'tenant_admin') return true;
    if (allowedRoles.includes(userRole)) return true;
    if (userRole === 'user' && (allowedRoles.includes('editor') || allowedRoles.includes('operator'))) return true;
    if (userRole === 'editor' && allowedRoles.includes('user')) return true; // az örökölt 'user' a szerveren szerkesztő
    return false;
  };

  // Modulok szűrése megjelenési hely, szerepkör és appMode szerint
  const getModulesByPlacement = (
    placement: MenuPlacement,
    filterAppMode?: AppMode,
    targetRole?: string
  ): ManifestModule[] => {
    const checkRole = (targetRole || currentRole) as string;
    const currentMode = filterAppMode || appMode || 'simplified';

    return effectiveModules
      .filter((mod) => {
        // 1. Helyszín ellenőrzés
        if (mod.placement !== placement) return false;
        if (mod.placement === 'hidden') return false;

        // 2. Szerepkör ellenőrzés
        if (!checkRoleAccess(checkRole, mod.roles)) return false;

        // 3. Alkalmazási mód (simplified vs extended) ellenőrzés – minden szerepkörre.
        // A modul módja a menükonfigurátorban állítható ('both' = mindkét nézetben látszik).
        // Korábban a konfigurátorban elhelyezett (placement) modulokra és a superadminra nem hatott,
        // a konfigurátor viszont minden modulnak elhelyezést ment, így a nézetváltó sehol nem működött.
        if (currentMode === 'simplified' && mod.mode === 'extended') return false;
        if (currentMode === 'extended' && mod.mode === 'simplified') return false;

        return true;
      })
      .sort((a, b) => a.order - b.order);
  };

  // Globális felülbírálások mentése (Szuperadmin)
  const saveGlobalOverrides = async (
    newOverrides: Record<string, ModuleOverrideConfig>,
    newDeletedIds?: string[],
    newPurgedIds?: string[]
  ) => {
    setGlobalOverrides(newOverrides);
    localStorage.setItem(GLOBAL_STORAGE_KEY, JSON.stringify(newOverrides));
    const toDelete = newDeletedIds !== undefined ? newDeletedIds : deletedModuleIds;
    const toPurge = newPurgedIds !== undefined ? newPurgedIds : purgedModuleIds;
    try {
      await ApiClient.saveLayoutOverrides('global', newOverrides, toDelete, toPurge);
    } catch (e) {
      console.error('Hiba a globális menübeállítások mentésekor:', e);
    }
  };

  // Szervezeti felülbírálások mentése (Org Admin)
  const saveTenantOverrides = async (newOverrides: Record<string, ModuleOverrideConfig>) => {
    setTenantOverrides(newOverrides);
    localStorage.setItem(TENANT_STORAGE_KEY, JSON.stringify(newOverrides));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newOverrides));
    try {
      await ApiClient.saveLayoutOverrides('tenant', newOverrides);
    } catch (e) {
      console.error('Hiba a szervezeti menübeállítások mentésekor:', e);
    }
  };

  // Visszaállítás a KÖZPONTI beállításra (A szuperadmin által megadott globális nevekre és konfigurációra)
  const resetToCentralDefaults = async () => {
    setTenantOverrides({});
    localStorage.removeItem(TENANT_STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
    try {
      await ApiClient.saveLayoutOverrides('tenant', {});
    } catch (e) {
      console.error('Hiba a szervezeti beállítások törlésekor:', e);
    }
  };

  // Visszaállítás a GYÁRI alapértékekre (A forráskódban megadott hardcoded megnevezésekre és struktúrára)
  const resetToFactoryDefaults = async () => {
    setTenantOverrides({});
    localStorage.removeItem(TENANT_STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(DELETED_MODULES_KEY);
    setDeletedModuleIds([]);
    localStorage.removeItem(PURGED_MODULES_KEY);
    setPurgedModuleIds([]);

    if (isSuperAdmin) {
      setGlobalOverrides({});
      localStorage.removeItem(GLOBAL_STORAGE_KEY);
      try {
        await ApiClient.saveLayoutOverrides('global', {}, [], []);
      } catch (e) {
        console.error('Hiba a globális beállítások gyári törlésekor:', e);
      }
    }
    try {
      await ApiClient.saveLayoutOverrides('tenant', {});
    } catch (e) {
      console.error('Hiba a szervezeti beállítások gyári törlésekor:', e);
    }
  };

  // Mentés diszpécser szerepkör alapján
  const saveAllOverrides = (newOverrides: Record<string, ModuleOverrideConfig>) => {
    if (isSuperAdmin) {
      saveGlobalOverrides(newOverrides);
    } else {
      saveTenantOverrides(newOverrides);
    }
  };

  // Egyedi modul konfiguráció mentése
  const saveModuleOverride = (moduleId: string, config: ModuleOverrideConfig) => {
    if (isSuperAdmin) {
      const updated = { ...globalOverrides, [moduleId]: { ...globalOverrides[moduleId], ...config } };
      saveGlobalOverrides(updated);
    } else {
      const updated = { ...tenantOverrides, [moduleId]: { ...tenantOverrides[moduleId], ...config } };
      saveTenantOverrides(updated);
    }
  };

  const resetAllOverrides = () => {
    resetToFactoryDefaults();
  };

  // Modul hozzáférési jogosultság ellenőrzése
  const canAccessModule = (moduleId: string, targetRole?: string): boolean => {
    if (!user) return ['public_portal'].includes(moduleId);
    const roleToCheck = (targetRole || currentRole) as string;
    if (isSuperAdmin || roleToCheck === 'superadmin') return true;
    const targetMod = effectiveModules.find((m) => m.id === moduleId) || rawModules.find((m) => m.id === moduleId);
    if (!targetMod) return true;
    return checkRoleAccess(roleToCheck, targetMod.roles);
  };

  // Ha a felhasználó kijelentkezik vagy szerepkört vált, ellenőrizzük az aktív modult!
  useEffect(() => {
    if (!user) {
      setActiveModuleId('records_manager');
      setActiveCategoryId('transparency_data');
      setActivePageId(null);
      return;
    }

    const currentMod = effectiveModules.find((m) => m.id === activeModuleId) || rawModules.find((m) => m.id === activeModuleId);
    if (currentMod && !checkRoleAccess(currentRole, currentMod.roles)) {
      setActiveModuleId('records_manager');
      const recMod = effectiveModules.find((m) => m.id === 'records_manager');
      if (recMod?.category) {
        setActiveCategoryId(recMod.category);
      }
      setActivePageId(null);
    }
  }, [user, currentRole, effectiveModules, activeModuleId]);

  const handleSetActiveModule = (modId: string) => {
    const targetMod = effectiveModules.find((m) => m.id === modId) || rawModules.find((m) => m.id === modId);
    if (targetMod && !checkRoleAccess(currentRole, targetMod.roles)) {
      console.warn(`[LayoutContext] Hozzáférés megtagadva a(z) ${modId} modulhoz (${currentRole} szerepkör).`);
      return;
    }
    setActiveModuleId(modId);
    if (targetMod && targetMod.category) {
      setActiveCategoryId(targetMod.category);
    }
  };

  // Aktuális hatásos overrides a UI számára
  const currentOverrides = isSuperAdmin ? globalOverrides : { ...globalOverrides, ...tenantOverrides };

  return (
    <LayoutContext.Provider
      value={{
        manifest,
        categories,
        modules: rawModules,
        effectiveModules,
        deletedModules,
        deletedModuleIds,
        activeCategoryId,
        activeModuleId,
        activePageId,
        setActiveCategory: setActiveCategoryId,
        setActiveModule: handleSetActiveModule,
        setActivePageId,
        isLoading,
        refreshLayout,
        overrides: currentOverrides,
        globalOverrides,
        tenantOverrides,
        getModulesByPlacement,
        saveModuleOverride,
        saveAllOverrides,
        saveGlobalOverrides,
        saveTenantOverrides,
        resetToCentralDefaults,
        resetToFactoryDefaults,
        resetAllOverrides,
        deleteModule,
        restoreModule,
        restoreAllModules,
        purgeModule,
        purgeAllDeletedModules,
        hasModuleOperation: checkModuleOperation,
        isConfiguratorOpen,
        setIsConfiguratorOpen,
        isSuperAdmin,
        isOrgAdmin,
        canAccessModule,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
